import { format, parse, parseISO, startOfToday } from "date-fns";

// Helper function to parse date strings in various formats
export const parseDate = (dateStr: string) => {
  try {
    // Log the date string for debugging
    console.log('Parsing date:', dateStr);

    // Handle different date formats
    if (typeof dateStr !== 'string') {
      console.error('Invalid date input:', dateStr);
      return startOfToday();
    }

    // Try different date formats
    const formats = [
      { format: "dd-MM-yyyy", test: /^\d{2}-\d{2}-\d{4}$/ },
      { format: "yyyy-MM-dd", test: /^\d{4}-\d{2}-\d{2}$/ },
      { format: "yyyy-M", test: /^\d{4}-\d{1,2}$/ }, // For month format like "2025-6"
      { format: "yyyy-w", test: /^\d{4}-\d{1,2}$/ }, // For week format like "2025-23"
    ];

    // Find matching format
    for (const { format: fmt, test } of formats) {
      if (test.test(dateStr)) {
        try {
          const parsedDate = parse(dateStr, fmt, new Date());
          if (!isNaN(parsedDate.getTime())) {
            return parsedDate;
          }
        } catch (e) {
          console.warn(`Failed to parse date ${dateStr} with format ${fmt}:`, e);
          continue;
        }
      }
    }

    // Try parsing as ISO date
    try {
      const isoDate = new Date(dateStr);
      if (!isNaN(isoDate.getTime())) {
        return isoDate;
      }
    } catch (e) {
      console.warn('Failed to parse as ISO date:', dateStr, e);
    }

    // If all parsing attempts fail, return today's date
    console.error('Could not parse date:', dateStr);
    return startOfToday();
  } catch (error) {
    console.error('Error parsing date:', dateStr, error);
    return startOfToday();
  }
};

// Transform daily stats data
export const transformDailyStats = (data: any[]) => {
  if (!Array.isArray(data)) {
    console.error("Daily stats data is not an array:", data);
    return [];
  }

  return data.map(item => {
    try {
      if (!item || typeof item !== 'object') {
        console.warn('Invalid daily stat item:', item);
        return null;
      }

      if (!item.Date) {
        console.warn('Missing Date in daily stat:', item);
        return null;
      }

      const date = parseDate(item.Date);
      if (isNaN(date.getTime())) {
        console.warn('Invalid date in daily stat:', item.Date);
        return null;
      }

      return {
        Date: format(date, "dd MMM"),
        InvoicedAmount: Number(item.InvoicedAmount || 0),
        originalDate: date,
      };
    } catch (error) {
      console.error('Error transforming daily stat:', item, error);
      return null;
    }
  })
  .filter(Boolean)
  .sort((a, b) => a.originalDate.getTime() - b.originalDate.getTime());
};

// Transform weekly stats data
export const transformWeeklyStats = (data: any[]) => {
  if (!Array.isArray(data)) {
    console.error("Weekly stats data is not an array:", data);
    return [];
  }

  return data.map(item => {
    try {
      if (!item || typeof item !== 'object') {
        console.warn('Invalid weekly stat item:', item);
        return null;
      }

      if (!item.Weekof) {
        console.warn('Missing Weekof in weekly stat:', item);
        return null;
      }

      // Handle week format (e.g., "2023-23" for week 23 of 2023)
      const [year, week] = item.Weekof.split('-').map(Number);
      if (!year || !week) {
        console.warn('Invalid week format:', item.Weekof);
        return null;
      }

      // Create a date for the first day of the week
      const date = new Date(year, 0, 1 + (week - 1) * 7);
      if (isNaN(date.getTime())) {
        console.warn('Invalid date for week:', item.Weekof);
        return null;
      }

      return {
        Weekof: `Week ${week}`,
        WeekTotal: Number(item.WeekTotal || 0),
        originalDate: date,
      };
    } catch (error) {
      console.error('Error transforming weekly stat:', item, error);
      return null;
    }
  })
  .filter(Boolean)
  .sort((a, b) => a.originalDate.getTime() - b.originalDate.getTime());
};

// Transform monthly stats data
export const transformMonthlyStats = (data: any[]) => {
  if (!Array.isArray(data)) {
    console.error("Monthly stats data is not an array:", data);
    return [];
  }

  return data.map(item => {
    try {
      if (!item || typeof item !== 'object') {
        console.warn('Invalid monthly stat item:', item);
        return null;
      }

      if (!item.Monthof) {
        console.warn('Missing Monthof in monthly stat:', item);
        return null;
      }

      // Handle month format (e.g., "2023-6" for June 2023)
      const [year, month] = item.Monthof.split('-').map(Number);
      if (!year || !month) {
        console.warn('Invalid month format:', item.Monthof);
        return null;
      }

      // Create a date for the first day of the month
      const date = new Date(year, month - 1); // month is 0-based in JavaScript
      if (isNaN(date.getTime())) {
        console.warn('Invalid date for month:', item.Monthof);
        return null;
      }

      return {
        Monthof: format(date, "MMM yyyy"),
        MonthTotal: Number(item.MonthTotal || 0),
        originalDate: date,
      };
    } catch (error) {
      console.error('Error transforming monthly stat:', item, error);
      return null;
    }
  })
  .filter(Boolean)
  .sort((a, b) => a.originalDate.getTime() - b.originalDate.getTime());
};

// Format API parameters with date range
export const formatApiParamsWithDateRange = (startDate: Date, endDate: Date, filters: { 
  companyId: string; 
  locationId: string; 
  userId: string; 
}) => {
  const { companyId, locationId, userId } = filters;
  
  // Base parameters
  const params: any = {
    sDate: format(startDate || startOfToday(), "yyyy-MM-dd"),
    eDate: format(endDate || startOfToday(), "yyyy-MM-dd"),
    // Pass through selected values exactly as chosen in UI (including "all")
    CompanyID: companyId,
    LocationID: locationId,
  };
  
  // Only include UserID if a specific user is selected (not "all")
  if (userId !== "all") {
    params.UserID = userId;
  }
  
  return params;
};

// Keep the old function for backward compatibility
export const formatApiParams = (date: Date, filters: { 
  companyId: string; 
  locationId: string; 
  userId: string; 
}) => {
  const { companyId, locationId, userId } = filters;
  return {
    Date: format(date || startOfToday(), "yyyy-MM-dd"),
    CompanyID: companyId === "all" ? "" : companyId,
    LocationID: locationId === "all" ? "" : locationId,
    UserID: userId === "all" ? "" : userId,
  };
};

// Y-axis config type matching StatisticsChart's yAxisConfig prop
export type YAxisConfig = {
  domain?: [number, number];
  tickFormatter?: (value: number) => string;
  ticks?: number[];
};

// Calculate dynamic Y-axis configuration based on sales data
export const calculateYAxisConfig = (data: any[], yKey: string = 'value'): YAxisConfig => {
  if (!Array.isArray(data) || data.length === 0) {
    // Default configuration when no data
    return {
      domain: [0, 100000] as [number, number],
      tickFormatter: (value: number) => value.toLocaleString(),
      ticks: [0, 20000, 40000, 60000, 80000, 100000]
    };
  }

  // Extract all values from the data
  const values = data.map(item => Number(item[yKey] || 0)).filter(val => !isNaN(val));
  
  if (values.length === 0) {
    return {
      domain: [0, 100000] as [number, number],
      tickFormatter: (value: number) => value.toLocaleString(),
      ticks: [0, 20000, 40000, 60000, 80000, 100000]
    };
  }

  const maxValue = Math.max(...values);
  
  // If all values are 0, set a default range
  if (maxValue === 0) {
    return {
      domain: [0, 100000] as [number, number],
      tickFormatter: (value: number) => value.toLocaleString(),
      ticks: [0, 20000, 40000, 60000, 80000, 100000]
    };
  }

  // Calculate a nice range with some padding
  const padding = maxValue * 0.1; // 10% padding
  let domainMax = maxValue + padding;

  // Round to nice numbers
  const magnitude = Math.pow(10, Math.floor(Math.log10(domainMax)));
  const niceMax = Math.ceil(domainMax / magnitude) * magnitude;
  
  // Generate ticks with equal intervals
  const tickCount = 6; // Number of ticks we want
  const tickStep = niceMax / (tickCount - 1);
  const ticks: number[] = [];
  
  for (let i = 0; i < tickCount; i++) {
    ticks.push(Math.round(tickStep * i));
  }

  return {
    domain: [0, niceMax] as [number, number],
    tickFormatter: (value: number) => value.toLocaleString(),
    ticks
  };
};